import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react'
import axios from 'axios'

interface Organization {
  id: string
  name: string
  slug: string
  industry: string
  tier: string
  features: string[]
}

interface User {
  username: string
  email?: string
  name: string
  roles: string[]
  organization?: Organization
  organizationId?: string
  industry?: string
}

interface LoginResult {
  success: boolean
  mfaRequired?: boolean
  error?: string
}

interface AuthContextType {
  user: User | null
  organization: Organization | null
  industry: string | null
  isAuthenticated: boolean
  isAdmin: boolean
  isLoading: boolean
  login: (username: string, password: string, mfaToken?: string) => Promise<LoginResult>
  logout: () => void
  hasRole: (role: string) => boolean
  hasPermission: (permission: string) => boolean
  getToken: () => string | null
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const AUTH_STORAGE_KEY = 'sme-dashboard-auth'
const TOKEN_STORAGE_KEY = 'auth_token'
// 2026-05-23: backend `/auth/refresh` (auth_v2) requires the refresh token
// in the request body, not the Authorization header. Prior to this fix the
// frontend never stored the refresh token returned by /auth/login, so
// every refresh attempt POSTed an empty `{}` body and got HTTP 422
// every 15-30s once the access token neared expiry.
const REFRESH_TOKEN_STORAGE_KEY = 'refresh_token'

// Permission mappings per role (mirror of backend)
// Includes all 8 core modules: Command Center, Asset Intelligence, Predictive Ops,
// Comparative, Root Cause, Maintenance, Self-Learning, Executive
const ROLE_PERMISSIONS: Record<string, string[]> = {
  admin: ['dashboard:read', 'dashboard:write',  // Command Center
          'users:read', 'users:write', 'users:delete', 'roles:read', 'roles:write',
          'assets:read', 'assets:write', 'assets:delete', 'predictions:read', 'predictions:write',
          'models:read', 'models:write',  // Self-Learning module
          'training:read', 'training:write', 'reports:read', 'reports:write',
          'analytics:read', 'analytics:write',  // Analytics & comparative modules
          'compliance:read', 'compliance:write',  // Compliance tracking
          'alerts:read', 'alerts:write',  // Alert management
          'admin:access', 'admin:settings', 'notifications:read', 'notifications:manage',
          'connectors:read', 'connectors:manage'],
  manager: ['dashboard:read',  // Command Center
            'users:read', 'users:write', 'roles:read', 'assets:read', 'assets:write',
            'predictions:read', 'predictions:write', 'models:read',  // Self-Learning
            'training:read', 'reports:read', 'reports:write',
            'analytics:read', 'analytics:write',  // Analytics & comparative
            'compliance:read',  // Compliance tracking (read-only)
            'alerts:read', 'alerts:write',  // Alert management
            'admin:access', 'notifications:read', 'notifications:manage'],
  operator: ['dashboard:read',  // Command Center
             'assets:read', 'assets:write', 'predictions:read', 'predictions:write',
             'models:read',  // Self-Learning
             'training:read', 'training:write', 'reports:read',
             'analytics:read',  // Analytics (read-only)
             'alerts:read',  // Alerts (read-only)
             'notifications:read'],
  analyst: ['dashboard:read',  // Command Center
            'assets:read', 'predictions:read', 'models:read',  // Self-Learning
            'training:read', 'reports:read', 'reports:write',
            'analytics:read', 'analytics:write',  // Full analytics access
            'compliance:read',  // Compliance tracking (read-only)
            'alerts:read'],  // Alerts (read-only)
  viewer: ['dashboard:read',  // Command Center
           'assets:read', 'predictions:read', 'models:read',  // Self-Learning
           'reports:read',
           'analytics:read',  // Analytics (read-only)
           'alerts:read'],  // Alerts (read-only)
}

const ORG_STORAGE_KEY = 'sme-dashboard-org'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState<User | null>(null)
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [permissions, setPermissions] = useState<string[]>([])

  // Initialize auth state from localStorage on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        const stored = localStorage.getItem(AUTH_STORAGE_KEY)
        const storedOrg = localStorage.getItem(ORG_STORAGE_KEY)
        const token = localStorage.getItem(TOKEN_STORAGE_KEY)
        if (stored && token) {
          // Verify token is still valid by decoding it
          const payload = JSON.parse(atob(token.split('.')[1]))
          const exp = payload.exp * 1000 // Convert to milliseconds
          if (Date.now() < exp) {
            setUser(JSON.parse(stored))
            if (storedOrg) {
              setOrganization(JSON.parse(storedOrg))
            }
            // Fetch fresh permissions from backend
            const userPermissions = await fetchPermissions(token)
            setPermissions(userPermissions)
          } else {
            // Token expired, clean up
            localStorage.removeItem(AUTH_STORAGE_KEY)
            localStorage.removeItem(TOKEN_STORAGE_KEY)
            localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY)
            localStorage.removeItem(ORG_STORAGE_KEY)
          }
        }
      } catch (e) {
        console.warn('Failed to load auth from localStorage:', e)
        localStorage.removeItem(AUTH_STORAGE_KEY)
        localStorage.removeItem(TOKEN_STORAGE_KEY)
        localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY)
        localStorage.removeItem(ORG_STORAGE_KEY)
      } finally {
        setIsLoading(false)
      }
    }

    initAuth()
  }, [])

  // Persist auth state to localStorage.
  //
  // May 5 2026 fix: this effect now ONLY writes when user is set. It does
  // NOT clear localStorage when user is null because:
  //   1. On every page navigation (page.goto / browser refresh), AuthProvider
  //      mounts with user=null while initAuth runs async to restore from
  //      localStorage. The previous code cleared auth_token at this moment,
  //      defeating the restore path.
  //   2. Real logout calls the dedicated `logout()` callback which removes
  //      all three keys explicitly. So the auto-clear here was redundant
  //      AND destructive.
  //
  // Without this fix: every navigation wiped auth_token → all post-login
  // API calls aborted client-side (AUTH_FREE short-circuit) → pages
  // rendered empty data and fell back to hardcoded F67503/F69871 dummies.
  useEffect(() => {
    try {
      if (user) {
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user))
      }
      // user=null is intentionally a no-op here. logout() does the cleanup.
    } catch (e) {
      console.warn('Failed to save auth to localStorage:', e)
    }
  }, [user])

  // Persist organization to localStorage
  useEffect(() => {
    try {
      if (organization) {
        localStorage.setItem(ORG_STORAGE_KEY, JSON.stringify(organization))
      } else {
        localStorage.removeItem(ORG_STORAGE_KEY)
      }
    } catch (e) {
      console.warn('Failed to save organization to localStorage:', e)
    }
  }, [organization])

  // Fetch user permissions from backend
  const fetchPermissions = async (token: string): Promise<string[]> => {
    try {
      const response = await axios.get('/api/v2/auth/me/permissions', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      if (response.data && response.data.permission_names) {
        return response.data.permission_names
      }
      return []
    } catch (error) {
      console.error('Failed to fetch permissions:', error)
      // Fallback to role-based permissions if API call fails
      return []
    }
  }

  const login = async (username: string, password: string, mfaToken?: string): Promise<LoginResult> => {
    try {
      // Call real backend API with optional MFA token
      const response = await axios.post('/api/v2/auth/login', {
        username,
        password,
        mfa_token: mfaToken || undefined
      })

      if (response.data && response.data.access_token) {
        const { access_token, refresh_token, user: userInfo, organization: orgInfo } = response.data

        // Store token
        localStorage.setItem(TOKEN_STORAGE_KEY, access_token)
        // Persist refresh token so api.ts:refreshAuthToken() can send it
        // in the body of /auth/refresh. Without this, every refresh fires
        // 422 and the user gets force-logged-out the moment the access
        // token expires.
        if (refresh_token) {
          localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refresh_token)
        }

        // Decode JWT to get user info (simple base64 decode of payload)
        const payload = JSON.parse(atob(access_token.split('.')[1]))
        const roles = payload.roles || []

        // Use permissions from login response if available, otherwise fetch
        let userPermissions: string[] = []
        if (userInfo?.permissions && Array.isArray(userInfo.permissions)) {
          // Backend now returns permissions in login response
          userPermissions = userInfo.permissions
        } else {
          // Fallback to fetching permissions separately
          userPermissions = await fetchPermissions(access_token)
        }
        setPermissions(userPermissions)

        // Use organization info from login response if available
        // Organization can be in userInfo.organization or as separate orgInfo
        let org: Organization | null = null
        const orgData = userInfo?.organization || orgInfo
        if (orgData) {
          org = {
            id: orgData.id,
            name: orgData.name,
            slug: orgData.slug || orgData.id, // Use id as slug fallback
            industry: orgData.industry,
            tier: orgData.tier || 'professional',
            features: orgData.features || [],
          }
          setOrganization(org)
        }

        // Build user object with organization info
        const userData: User = {
          username: userInfo?.username || payload.username || payload.sub,
          email: userInfo?.email || payload.email,
          name: (userInfo?.first_name && userInfo?.last_name)
            ? `${userInfo.first_name} ${userInfo.last_name}`
            : (userInfo?.username || payload.username)?.charAt(0).toUpperCase() + (userInfo?.username || payload.username)?.slice(1) || payload.sub,
          roles: userInfo?.roles || roles,
          organization: org || undefined,
          organizationId: userInfo?.organization_id || payload.organization_id || undefined,
          industry: userInfo?.industry || payload.industry || org?.industry || undefined,
        }

        setUser(userData)
        return { success: true }
      }
      return { success: false, error: 'No access token received' }
    } catch (error: any) {
      console.error('Login failed:', error)

      // Check if error is MFA token required
      if (error?.response?.status === 403) {
        const detail = error?.response?.data?.detail || ''
        if (detail.includes('MFA token required')) {
          return { success: false, mfaRequired: true }
        }
        if (detail.includes('Invalid MFA token')) {
          return { success: false, error: 'Invalid authentication code. Please try again.' }
        }
      }

      // Return specific error message from backend
      const errorMessage = error?.response?.data?.detail || 'Login failed. Please try again.'
      return { success: false, error: errorMessage }
    }
  }

  const logout = useCallback(() => {
    setUser(null)
    setOrganization(null)
    localStorage.removeItem(TOKEN_STORAGE_KEY)
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY)
    localStorage.removeItem(AUTH_STORAGE_KEY)
    localStorage.removeItem(ORG_STORAGE_KEY)
  }, [])

  const hasRole = useCallback((role: string): boolean => {
    return user?.roles?.includes(role) ?? false
  }, [user?.roles])

  const hasPermission = useCallback((permission: string): boolean => {
    // Check dynamic permissions from backend
    if (permissions.length > 0 && permissions.includes(permission)) {
      return true
    }

    // Also check role-based permissions (union with backend permissions)
    // This ensures sidebar items appear even if DB role_permissions table is incomplete
    if (user?.roles) {
      const hasRolePerm = user.roles.some(role =>
        ROLE_PERMISSIONS[role]?.includes(permission) ?? false
      )
      if (hasRolePerm) return true
    }

    return false
  }, [permissions, user?.roles])

  const getToken = useCallback((): string | null => {
    return localStorage.getItem(TOKEN_STORAGE_KEY)
  }, [])

  const isAdmin = user?.roles?.includes('admin') ?? false

  return (
    <AuthContext.Provider value={{
      user,
      organization,
      industry: organization?.industry || user?.industry || null,
      isAuthenticated: user !== null,
      isAdmin,
      isLoading,
      login,
      logout,
      hasRole,
      hasPermission,
      getToken
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
